import { useMemo } from "react";
import dayjs from "dayjs";
import { ViewMode } from "@thepocman/gantt-task-react";
import Papa from "papaparse";
import milestonesRaw from "@/assets/data/site_milestones.csv?raw";
import { CardLayout } from "@/views/TrialHomeScreen/components/CardLayout";
import TimelineLibrary from "@/components/Timeline/TimelineLibrary";
import TimelineHeaderControls from "@/components/Timeline/TimelineHeaderControls";
import { useTimelineControls } from "@/components/Timeline/useTimelineControls";
import { milestonesTimelineUtils } from "@/components/Timeline/milestonesTimelineUtils";
import type { TimelineItem } from "@/components/Timeline/timeline";
import { PortfolioMetricTile } from "@/views/Portfolio/shared";
import SiteTabTemplate from "../components/SiteTabTemplate";

// ---------------------------------------------------------------------------
// Types — aligned with the planned `site_milestone` backend table
// (CTMS Site Operational Data Model, VIVO-7528)
// ---------------------------------------------------------------------------
type MilestoneStatus = "COMPLETE" | "PENDING" | "WAIVED" | "NA";
type MilestoneCategory =
  | "startup"
  | "regulatory"
  | "activation"
  | "monitoring"
  | "enrollment"
  | "closeout";

type SiteMilestoneRow = {
  site: string;
  milestone_type: string; // SITE_MILESTONE_TYPE CV code
  milestone_label: string;
  planned_date: string;
  actual_date: string;
  status: MilestoneStatus;
  category: MilestoneCategory;
  notes: string;
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const TODAY = new Date("2026-06-16");

function parseDateStr(s: string): Date | null {
  if (!s) return null;
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}

function daysBetween(a: Date, b: Date): number {
  return Math.round((b.getTime() - a.getTime()) / 86_400_000);
}

function effectiveStatus(
  row: SiteMilestoneRow,
): "done" | "overdue" | "upcoming" | "waived" {
  if (row.status === "WAIVED" || row.status === "NA") return "waived";
  if (row.actual_date) return "done";
  const planned = parseDateStr(row.planned_date);
  if (!planned) return "upcoming";
  return planned < TODAY ? "overdue" : "upcoming";
}

type VarianceDisplay = { text: string; color: string } | null;

function variance(row: SiteMilestoneRow): VarianceDisplay {
  const planned = parseDateStr(row.planned_date);
  const actual = parseDateStr(row.actual_date);
  if (!planned || !actual) return null;
  const days = daysBetween(planned, actual);
  if (days === 0) return { text: "On time", color: "#52c41a" };
  if (days > 0) return { text: `+${days}d`, color: "#ff4d4f" };
  return { text: `${days}d`, color: "#52c41a" };
}

const STATUS_CONFIG: Record<
  string,
  { label: string; color: string; bg: string }
> = {
  done: { label: "Done", color: "#52c41a", bg: "#f6ffed" },
  overdue: { label: "Overdue", color: "#ff4d4f", bg: "#fff2f0" },
  upcoming: { label: "Upcoming", color: "#8c8c8c", bg: "#fafafa" },
  waived: { label: "Waived", color: "#faad14", bg: "#fffbe6" },
};

const StatusBadge = ({ status }: { status: string }) => {
  const cfg = STATUS_CONFIG[status] ?? STATUS_CONFIG.upcoming;
  return (
    <span
      className="text-[11px] font-semibold px-2 py-0.5 rounded"
      style={{ color: cfg.color, background: cfg.bg }}
    >
      {cfg.label}
    </span>
  );
};

const CATEGORY_ORDER: MilestoneCategory[] = [
  "startup",
  "regulatory",
  "activation",
  "enrollment",
  "closeout",
];

const CATEGORY_LABELS: Record<MilestoneCategory, string> = {
  startup: "Site Start-Up",
  regulatory: "Regulatory",
  activation: "Activation",
  monitoring: "Monitoring Visits",
  enrollment: "Enrollment",
  closeout: "Close-Out",
};

// ---------------------------------------------------------------------------
// Timeline helpers
// ---------------------------------------------------------------------------
const SITE_MILESTONE_COLUMNS = [
  { fieldName: "event_type", visibility: "visible" as const },
  { fieldName: "event_name", visibility: "visible" as const },
  { fieldName: "start_date", visibility: "visible" as const },
  { fieldName: "end_date", visibility: "visible" as const },
];

const siteTimelineUtils = {
  ...milestonesTimelineUtils,
  columnLabels: {
    event_type: "Category",
    event_name: "Milestone",
    start_date: "Planned",
    end_date: "Actual",
  },
};

function toTimelineItems(rows: SiteMilestoneRow[]): TimelineItem[] {
  const items: TimelineItem[] = [];
  for (const row of rows) {
    if (row.actual_date) {
      items.push({
        core_table: "Actual Date",
        event_name: row.milestone_label,
        event_type: CATEGORY_LABELS[row.category] ?? row.category,
        start_date: row.actual_date,
        end_date: row.actual_date,
        ongoing_derived: false,
      });
    }
    if (row.planned_date) {
      items.push({
        core_table: "Planned Date",
        event_name: row.milestone_label,
        event_type: CATEGORY_LABELS[row.category] ?? row.category,
        start_date: row.planned_date,
        end_date: row.planned_date,
        ongoing_derived: false,
      });
    }
  }
  return items;
}

function SiteMilestoneTimeline({ rows }: { rows: SiteMilestoneRow[] }) {
  const timelineData = useMemo(() => toTimelineItems(rows), [rows]);

  const firstEventDate = useMemo(() => {
    if (!timelineData.length) return null;
    return new Date(
      Math.min(...timelineData.map((r) => new Date(r.start_date).getTime())),
    );
  }, [timelineData]);

  const lastEventDate = useMemo(() => {
    if (!timelineData.length) return null;
    return new Date(
      Math.max(
        ...timelineData.map((r) =>
          new Date(r.end_date ?? r.start_date).getTime(),
        ),
      ),
    );
  }, [timelineData]);

  const controls = useTimelineControls({
    firstEventDate,
    initialViewMode: ViewMode.Month,
    initializeToFirstEvent: true,
  });

  return (
    <div className="flex flex-col gap-2">
      <TimelineHeaderControls
        viewMode={controls.viewMode}
        selectedDate={controls.selectedDate}
        isTodayActive={controls.isTodayActive}
        isFirstEventActive={controls.isFirstEventActive}
        hasFirstEventDate={Boolean(firstEventDate)}
        minSelectableDate={firstEventDate}
        maxSelectableDate={lastEventDate}
        testIdPrefix="site-milestones-timeline"
        onViewModeChange={controls.handleViewModeChange}
        onDateChange={controls.handleDateChange}
        onTodayClick={controls.handleTodayClick}
        onEarliestClick={controls.handleEarliestClick}
      />
      <TimelineLibrary
        data={timelineData}
        viewMode={controls.viewMode}
        viewDate={controls.viewDate}
        centerOnDate={controls.centerOnDate}
        centerOnDateRequestId={controls.centerOnDateRequestId}
        columns={SITE_MILESTONE_COLUMNS}
        utils={siteTimelineUtils}
        viewportOffset={320}
        onHorizontalScroll={controls.handleHorizontalScroll}
        onRowSelect={(item) =>
          controls.handleDateChange(dayjs(item.start_date))
        }
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------
function MilestoneTable({ rows }: { rows: SiteMilestoneRow[] }) {
  if (!rows.length)
    return (
      <p className="text-[13px] text-[#8C8C8C] p-3">No milestones found.</p>
    );

  return (
    <table className="w-full text-[13px]">
      <thead>
        <tr className="border-b border-[#f0f0f0]">
          <th className="text-left pb-2 pr-4 font-semibold text-[#2B4752] w-[35%]">
            Milestone
          </th>
          <th className="text-left pb-2 pr-4 font-semibold text-[#2B4752] w-[16%]">
            Planned
          </th>
          <th className="text-left pb-2 pr-4 font-semibold text-[#2B4752] w-[16%]">
            Actual
          </th>
          <th className="text-left pb-2 pr-4 font-semibold text-[#2B4752] w-[13%]">
            Variance
          </th>
          <th className="text-left pb-2 font-semibold text-[#2B4752] w-[12%]">
            Status
          </th>
          <th className="text-left pb-2 font-semibold text-[#2B4752]">Notes</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => {
          const status = effectiveStatus(row);
          const v = variance(row);
          return (
            <tr
              key={`${row.milestone_type}-${i}`}
              className="border-b border-[#f5f5f5] last:border-0"
            >
              <td className="py-2 pr-4 text-[#2B4752]">
                {row.milestone_label}
              </td>
              <td className="py-2 pr-4 text-[#595959]">
                {row.planned_date || "-"}
              </td>
              <td className="py-2 pr-4 text-[#595959]">
                {row.actual_date || "—"}
              </td>
              <td className="py-2 pr-4">
                {v ? (
                  <span style={{ color: v.color }} className="font-medium">
                    {v.text}
                  </span>
                ) : (
                  <span className="text-[#8C8C8C]">—</span>
                )}
              </td>
              <td className="py-2 pr-4">
                <StatusBadge status={status} />
              </td>
              <td className="py-2 text-[#8C8C8C] text-[12px]">
                {row.notes || ""}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function MilestoneDashboard({
  lifecycle,
  monitoring,
}: {
  lifecycle: SiteMilestoneRow[];
  monitoring: SiteMilestoneRow[];
}) {
  const allRows = [...lifecycle, ...monitoring];
  const total = allRows.length;
  const done = allRows.filter((r) => effectiveStatus(r) === "done").length;
  const overdue = allRows.filter(
    (r) => effectiveStatus(r) === "overdue",
  ).length;
  const upcoming = allRows.filter(
    (r) => effectiveStatus(r) === "upcoming",
  ).length;
  const waived = allRows.filter((r) => effectiveStatus(r) === "waived").length;

  return (
    <div className="overflow-hidden rounded-[8px] border border-[#F0F2F4] bg-white shadow-[0_8px_24px_rgba(15,23,42,0.08)]">
      <div
        className="grid divide-x divide-[#F0F2F4] border-b border-[#F0F2F4]"
        style={{ gridTemplateColumns: "repeat(5, minmax(0, 1fr))" }}
      >
        <PortfolioMetricTile label="TOTAL MILESTONES" value={total} />
        <PortfolioMetricTile label="COMPLETE" value={done} />
        <PortfolioMetricTile
          label="OVERDUE"
          value={overdue}
          progressTone="magenta"
        />
        <PortfolioMetricTile label="UPCOMING" value={upcoming} />
        <PortfolioMetricTile
          label="WAIVED"
          value={waived}
          progressTone="gold"
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export default function MilestonesTab({ siteId }: { siteId: string }) {
  const { lifecycle, monitoring } = useMemo(() => {
    const parsed = Papa.parse<SiteMilestoneRow>(milestonesRaw, {
      header: true,
      skipEmptyLines: true,
    });

    const rows = parsed.data.filter((r) => r.site === siteId);
    return {
      lifecycle: rows.filter((r) => r.category !== "monitoring"),
      monitoring: rows.filter((r) => r.category === "monitoring"),
    };
  }, [siteId]);

  const grouped = useMemo(() => {
    const map = new Map<MilestoneCategory, SiteMilestoneRow[]>();
    CATEGORY_ORDER.forEach((cat) => map.set(cat, []));
    lifecycle.forEach((row) => {
      const list = map.get(row.category) ?? [];
      list.push(row);
      map.set(row.category, list);
    });
    return map;
  }, [lifecycle]);

  const tableContent = (
    <div className="flex flex-col gap-4">
      {CATEGORY_ORDER.map((cat) => {
        const rows = grouped.get(cat) ?? [];
        if (!rows.length) return null;
        return (
          <CardLayout key={cat} title={CATEGORY_LABELS[cat]}>
            <div className="p-4 overflow-x-auto">
              <MilestoneTable rows={rows} />
            </div>
          </CardLayout>
        );
      })}

      {monitoring.length > 0 && (
        <CardLayout
          title="Monitoring Visits"
          subtitle="SIV, IMV, COV — planned vs actual"
        >
          <div className="p-4 overflow-x-auto">
            <MilestoneTable rows={monitoring} />
          </div>
        </CardLayout>
      )}
    </div>
  );

  return (
    <SiteTabTemplate
      tableContent={tableContent}
      dashboardContent={
        <MilestoneDashboard lifecycle={lifecycle} monitoring={monitoring} />
      }
      timelineContent={
        <SiteMilestoneTimeline rows={[...lifecycle, ...monitoring]} />
      }
    />
  );
}
